// Admin platform settings — fees, feature flags and operational limits that
// take effect immediately, without a redeploy.
//
// This screen is DRIVEN BY the backend registry: it renders whatever
// /admin/settings declares, using each setting's type/bounds/options to pick the
// control and validate input. Adding a setting on the server makes it appear
// here with no frontend change, which is the whole point of the registry.
//
// Editing rules that matter:
//   - Role gating is per setting (`editable`). A non-super-admin sees pricing
//     read-only rather than being allowed to try and fail.
//   - Changes are staged locally and saved as ONE atomic batch, matching the
//     server, so a rejected value never leaves the platform half-changed.
//   - `danger` settings (anything that moves money or takes a product offline)
//     require explicit confirmation naming what is about to change.

import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, History, Loader2, RotateCcw, Save, ShieldAlert } from 'lucide-react';
import { toast } from 'sonner';
import { PortalShell } from '../../components/shared/PortalShell';
import { Card, SectionHeader } from '../../components/shared/primitives';
import { Button } from '../../components/stripe-ui/Button';
import { ADMIN_NAV } from '../_navs';
import { admin, ApiError, type PlatformSetting } from '../../lib/api';

type Draft = Record<string, number | boolean | string>;

/** Money settings are stored in minor units; show dollars to humans. */
function isMoneyKey(key: string): boolean {
  return key.endsWith('_usd_minor');
}

function displayValue(s: PlatformSetting, raw: number | boolean | string): string {
  if (s.type === 'int' && isMoneyKey(s.key)) return (Number(raw) / 100).toFixed(2);
  return String(raw);
}

/** Parse what the admin typed back into the stored representation. */
function parseValue(s: PlatformSetting, input: string): number | string {
  if (s.type !== 'int') return input;
  const n = Number(input);
  if (!Number.isFinite(n)) return NaN;
  return isMoneyKey(s.key) ? Math.round(n * 100) : Math.round(n);
}

function validate(s: PlatformSetting, value: number | boolean | string): string | null {
  if (s.type !== 'int') return null;
  const n = Number(value);
  if (!Number.isFinite(n)) return 'Must be a number.';
  if (!Number.isInteger(n)) return 'Must be a whole number.';
  if (s.min !== null && n < s.min) {
    return `Minimum is ${isMoneyKey(s.key) ? `$${(s.min / 100).toFixed(2)}` : s.min}.`;
  }
  if (s.max !== null && n > s.max) {
    return `Maximum is ${isMoneyKey(s.key) ? `$${(s.max / 100).toFixed(2)}` : s.max}.`;
  }
  return null;
}

export function AdminSettingsPage() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['admin', 'settings'], queryFn: admin.settings });
  const historyQ = useQuery({ queryKey: ['admin', 'settings', 'history'], queryFn: () => admin.settingsHistory() });

  const [draft, setDraft] = useState<Draft>({});
  const [note, setNote] = useState('');
  const [showHistory, setShowHistory] = useState(false);

  const allSettings = useMemo(
    () => (q.data?.groups ?? []).flatMap((g) => g.settings),
    [q.data],
  );

  // Only keys whose value actually differs from the server get sent.
  const changed = useMemo(() => {
    const out: Draft = {};
    for (const s of allSettings) {
      if (s.key in draft && draft[s.key] !== s.value) out[s.key] = draft[s.key];
    }
    return out;
  }, [draft, allSettings]);

  const changedKeys = Object.keys(changed);

  const errors = useMemo(() => {
    const out: Record<string, string> = {};
    for (const s of allSettings) {
      if (s.key in changed) {
        const err = validate(s, changed[s.key]);
        if (err) out[s.key] = err;
      }
    }
    return out;
  }, [changed, allSettings]);

  const hasErrors = Object.keys(errors).length > 0;
  const dangerChanges = allSettings.filter((s) => s.danger && s.key in changed);

  const save = useMutation({
    mutationFn: () => admin.updateSettings(changed, note || undefined),
    onSuccess: () => {
      setDraft({});
      setNote('');
      qc.invalidateQueries({ queryKey: ['admin', 'settings'] });
      toast.success(`Saved ${changedKeys.length} setting${changedKeys.length === 1 ? '' : 's'}`);
    },
    onError: (e) => {
      // The server is the authority on validity; surface its reason verbatim
      // rather than a generic failure.
      toast.error(e instanceof ApiError ? e.message : 'Could not save settings');
    },
  });

  function onSave() {
    if (hasErrors || changedKeys.length === 0) return;

    if (dangerChanges.length > 0) {
      const lines = dangerChanges
        .map((s) => `• ${s.label}: ${displayValue(s, s.value)} → ${displayValue(s, changed[s.key])}`)
        .join('\n');
      // Name exactly what is changing — a bare "are you sure?" teaches people
      // to click through it.
      if (!window.confirm(`These changes take effect immediately for all customers:\n\n${lines}\n\nApply now?`)) {
        return;
      }
    }

    save.mutate();
  }

  return (
    <PortalShell
      nav={ADMIN_NAV}
      activeId="platform"
      title="Platform settings"
      subtitle="Fees, product availability and operational limits — applied immediately."
      actions={
        <Button tone="secondary" onClick={() => setShowHistory((v) => !v)}>
          <History className="w-4 h-4" /> {showHistory ? 'Hide' : 'History'}
        </Button>
      }
    >
      {q.isLoading && (
        <Card>
          <p className="py-8 text-center text-[13px] text-slate-500 inline-flex items-center gap-2 justify-center w-full">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading settings…
          </p>
        </Card>
      )}

      {q.isError && (
        <Card>
          <p className="py-6 text-center text-[13px] text-rose-600">
            Could not load settings. You may not have admin access.
          </p>
        </Card>
      )}

      {showHistory && (
        <Card className="mb-4">
          <SectionHeader title="Recent changes" />
          {(historyQ.data?.history ?? []).length === 0 ? (
            <p className="py-4 text-[12.5px] text-slate-500">No changes recorded yet.</p>
          ) : (
            <div className="divide-y divide-slate-100">
              {(historyQ.data?.history ?? []).slice(0, 15).map((h, i) => (
                <div key={i} className="py-2 text-[12.5px]">
                  <span className="font-semibold text-slate-900">{h.key}</span>
                  <span className="text-slate-500">
                    {' '}
                    {h.previous_value ?? '(default)'} → <span className="font-semibold text-slate-800">{h.new_value}</span>
                  </span>
                  <div className="text-[11.5px] text-slate-400">
                    {new Date(h.created_at).toLocaleString()}
                    {h.changed_by_email ? ` · ${h.changed_by_email}` : ''}
                    {h.note ? ` · ${h.note}` : ''}
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      )}

      <div className="space-y-4">
        {(q.data?.groups ?? []).map((group) => (
          <Card key={group.group}>
            <SectionHeader title={group.group} />
            <div className="divide-y divide-slate-100">
              {group.settings.map((s) => {
                const current = s.key in draft ? draft[s.key] : s.value;
                const isChanged = s.key in changed;
                const error = errors[s.key];

                return (
                  <div key={s.key} className="py-3.5 grid gap-3 sm:grid-cols-[1fr_auto] sm:items-start">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="text-[13.5px] font-semibold text-slate-900">{s.label}</p>
                        {s.danger && (
                          <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 border border-amber-200 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-amber-700">
                            <AlertTriangle className="w-3 h-3" /> Sensitive
                          </span>
                        )}
                        {isChanged && !error && (
                          <span className="rounded-full bg-brand-50 border border-brand-200 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-brand-700">
                            Unsaved
                          </span>
                        )}
                      </div>
                      {s.help && <p className="mt-0.5 text-[11.5px] leading-relaxed text-slate-500">{s.help}</p>}
                      {!s.editable && (
                        <p className="mt-1 inline-flex items-center gap-1 text-[11px] font-semibold text-slate-400">
                          <ShieldAlert className="w-3 h-3" /> Requires {s.required_role.replace('_', ' ')}
                        </p>
                      )}
                      {error && <p className="mt-1 text-[11.5px] font-semibold text-rose-600">{error}</p>}
                    </div>

                    <div className="flex items-center gap-2 sm:justify-end">
                      {s.type === 'bool' && (
                        <button
                          type="button"
                          role="switch"
                          aria-checked={Boolean(current)}
                          aria-label={s.label}
                          disabled={!s.editable}
                          onClick={() => setDraft((d) => ({ ...d, [s.key]: !current }))}
                          className={`relative h-6 w-11 rounded-full transition ${
                            current ? 'bg-brand-600' : 'bg-slate-300'
                          } ${s.editable ? '' : 'opacity-50 cursor-not-allowed'}`}
                        >
                          <span
                            className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-all ${
                              current ? 'left-[22px]' : 'left-0.5'
                            }`}
                          />
                        </button>
                      )}

                      {s.type === 'int' && (
                        <div className="flex items-center gap-1.5">
                          {isMoneyKey(s.key) && <span className="text-[13px] text-slate-500">$</span>}
                          <input
                            type="text"
                            inputMode="decimal"
                            aria-label={s.label}
                            disabled={!s.editable}
                            value={displayValue(s, current)}
                            onChange={(e) =>
                              setDraft((d) => ({ ...d, [s.key]: parseValue(s, e.target.value) }))
                            }
                            className={`w-28 rounded-md border bg-white px-2.5 py-1.5 text-right text-[13px] tabular-nums outline-none disabled:bg-slate-50 disabled:text-slate-400 ${
                              error ? 'border-rose-300' : 'border-slate-200 focus:border-brand-400'
                            }`}
                          />
                        </div>
                      )}

                      {s.type === 'enum' && (
                        <select
                          aria-label={s.label}
                          disabled={!s.editable}
                          value={String(current)}
                          onChange={(e) => setDraft((d) => ({ ...d, [s.key]: e.target.value }))}
                          className="rounded-md border border-slate-200 bg-white px-2.5 py-1.5 text-[13px] outline-none focus:border-brand-400 disabled:bg-slate-50 disabled:text-slate-400"
                        >
                          {(s.options ?? []).map((o) => (
                            <option key={o} value={o}>{o}</option>
                          ))}
                        </select>
                      )}

                      {isChanged && s.editable && (
                        <button
                          type="button"
                          title="Revert this change"
                          aria-label={`Revert ${s.label}`}
                          onClick={() =>
                            setDraft((d) => {
                              const next = { ...d };
                              delete next[s.key];
                              return next;
                            })
                          }
                          className="p-1.5 text-slate-400 hover:text-slate-700"
                        >
                          <RotateCcw className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>
        ))}
      </div>

      {/* Save bar — only once something is actually different. */}
      {changedKeys.length > 0 && (
        <div className="sticky bottom-4 mt-4">
          <Card className="shadow-lg border-brand-200">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="min-w-0">
                <p className="text-[13px] font-semibold text-slate-900">
                  {changedKeys.length} unsaved change{changedKeys.length === 1 ? '' : 's'}
                </p>
                <p className="text-[11.5px] text-slate-500 truncate">{changedKeys.join(', ')}</p>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="Why? (recorded in history)"
                  className="w-56 rounded-md border border-slate-200 bg-white px-2.5 py-1.5 text-[12.5px] outline-none focus:border-brand-400"
                />
                <Button tone="secondary" onClick={() => { setDraft({}); setNote(''); }}>
                  Discard
                </Button>
                <Button onClick={onSave} disabled={hasErrors || save.isPending}>
                  {save.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  Save changes
                </Button>
              </div>
            </div>
            {hasErrors && (
              <p className="mt-2 text-[12px] font-semibold text-rose-600">
                Fix the highlighted values before saving.
              </p>
            )}
          </Card>
        </div>
      )}
    </PortalShell>
  );
}
